'use client'

import type { DefaultCellComponentProps } from 'payload'

import {
  Button,
  ChevronIcon,
  DefaultCell,
  Modal,
  Thumbnail,
  useDrawerSlug,
  useListDrawerContext,
  useListQuery,
  useModal,
  XIcon,
} from '@payloadcms/ui'
import { formatFilesize, getBestFitFromSizes } from 'payload/shared'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * Cella del campo `filename` di Media (la prima colonna della lista, quella cliccabile).
 *
 * Nella lista Media normale non cambia NULLA: rende la cella nativa (miniatura + link alla edit view).
 *
 * DENTRO il list drawer — quello che si apre da "Choose from existing" su featuredImage / ogImage /
 * upload del rich text — il click non seleziona più di colpo: apre un'ANTEPRIMA GRANDE con nome,
 * dimensioni, peso, alt e il flag "generata da AI", e solo "Usa questa immagine" scrive il valore nel
 * campo. Scegliere una copertina da una miniatura di 40px era un tiro alla cieca.
 *
 * Le frecce ‹ › (e i tasti ←/→) sfogliano gli altri media della pagina di lista senza chiudere
 * l'anteprima: confrontare i candidati è il gesto vero, non guardarne uno alla volta.
 */

type SelectHandler = NonNullable<ReturnType<typeof useListDrawerContext>['onSelect']>

type MediaDoc = {
  aiGenerated?: boolean | null
  alt?: null | string
  filename?: null | string
  filesize?: null | number
  height?: null | number
  id: number | string
  mimeType?: null | string
  sizes?: null | Record<string, undefined | { url?: null | string; width?: null | number }>
  thumbnailURL?: null | string
  updatedAt?: null | string
  url?: null | string
  width?: null | number
}

/** Le `sizes` come le vuole getBestFitFromSizes, che non digerisce i null di Payload. */
const fitSizes = (doc: MediaDoc): Record<string, { url?: string; width?: number }> =>
  Object.fromEntries(
    Object.entries(doc.sizes ?? {}).map(([name, size]) => [
      name,
      { url: size?.url ?? undefined, width: size?.width ?? undefined },
    ]),
  )

/** Miniatura di riga: stessa scelta di size della cella nativa (target 40-180px). */
const thumbSrc = (doc: MediaDoc): string =>
  getBestFitFromSizes({
    sizes: fitSizes(doc),
    thumbnailURL: doc.thumbnailURL ?? undefined,
    url: doc.url ?? '',
    width: doc.width ?? undefined,
  })

/**
 * Anteprima grande: la size più vicina a 1000-2000px — su Media è `highlight-thumb` (1024) — e non
 * l'originale, che su una copertina generata pesa qualche MB per niente. Se nessuna size arriva a
 * 1000px (upload piccolo, media vecchio) ricade sull'originale.
 */
const largeSrc = (doc: MediaDoc): string =>
  getBestFitFromSizes({
    sizes: fitSizes(doc),
    targetSizeMax: 2000,
    targetSizeMin: 1000,
    url: doc.url ?? '',
    width: doc.width ?? undefined,
  })

/** Cache tag come lo mette Payload sulle miniature: il file sostituito non resta appeso al CDN. */
const withCacheTag = (url: string, tag?: null | string): string =>
  tag ? `${url}${url.includes('?') ? '&' : '?'}${encodeURIComponent(tag)}` : url

/**
 * I drawer di Payload stanno a z-index 100 + profondità (il list drawer è 101, uno annidato 102).
 * L'anteprima deve stare sopra tutta quella pila senza dipendere dall'ordine dei portal.
 */
const PREVIEW_Z = 200

const styles = {
  actions: { display: 'flex', flexShrink: 0, gap: '0.5rem' },
  alt: {
    color: 'var(--theme-elevation-500)',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  backdrop: {
    alignItems: 'center',
    backdropFilter: 'blur(6px)',
    background: 'rgba(0, 0, 0, 0.72)',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '100%',
    padding: '1.5rem',
  },
  card: {
    background: 'var(--theme-bg)',
    borderRadius: 'var(--style-radius-m)',
    boxShadow: '0 1.25rem 3.75rem rgba(0, 0, 0, 0.45)',
    color: 'var(--theme-text)',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    overflow: 'hidden',
    width: 'min(68rem, 92vw)',
  },
  close: { position: 'absolute', right: '0.5rem', top: '0.5rem', zIndex: 1 },
  empty: { color: 'var(--theme-elevation-500)', padding: '3rem 1rem' },
  filename: { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  footer: {
    alignItems: 'flex-end',
    borderTop: '1px solid var(--theme-elevation-100)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    justifyContent: 'space-between',
    padding: '1rem',
  },
  image: { display: 'block', maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' },
  meta: { color: 'var(--theme-elevation-600)', fontSize: '0.8rem', marginTop: '0.25rem' },
  nav: { position: 'absolute', top: '50%', transform: 'translateY(-50%)' },
  // Altezza FISSA: sfogliando, un verticale dopo un orizzontale non fa saltare la card (e il
  // riquadro non collassa nell'istante in cui l'immagine successiva sta ancora arrivando).
  stage: {
    alignItems: 'center',
    background: 'var(--theme-elevation-50)',
    display: 'flex',
    height: '62vh',
    justifyContent: 'center',
    position: 'relative',
  },
} satisfies Record<string, React.CSSProperties>

export default function MediaPreviewCell(props: DefaultCellComponentProps) {
  const { isInDrawer, onSelect } = useListDrawerContext()

  // Lista Media normale: cella nativa, comportamento nativo.
  if (!isInDrawer || typeof onSelect !== 'function') {
    return <DefaultCell {...props} />
  }

  return <PreviewCell {...props} onSelect={onSelect} />
}

function PreviewCell({
  cellData,
  collectionSlug,
  onSelect,
  rowData,
}: { onSelect: SelectHandler } & DefaultCellComponentProps) {
  const modalSlug = useDrawerSlug('media-preview')
  const { closeModal, isModalOpen, openModal } = useModal()
  const { data } = useListQuery()

  const row = rowData as MediaDoc
  const docs = useMemo(() => (data?.docs ?? []) as MediaDoc[], [data?.docs])
  const [previewID, setPreviewID] = useState<MediaDoc['id']>(row.id)

  const index = docs.findIndex((doc) => doc.id === previewID)
  // Se la lista si è ricaricata sotto l'anteprima, il documento della riga resta il fallback.
  const doc = index > -1 ? docs[index] : row
  const isOpen = isModalOpen(modalSlug)

  const open = useCallback(() => {
    // Si riapre sempre sulla riga cliccata, non dove si era arrivati sfogliando la volta prima.
    setPreviewID(row.id)
    openModal(modalSlug)
  }, [modalSlug, openModal, row.id])

  const step = useCallback(
    (delta: number) => {
      setPreviewID((current) => {
        const from = docs.findIndex((candidate) => candidate.id === current)
        if (from < 0) return current
        return docs[from + delta]?.id ?? current
      })
    },
    [docs],
  )

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1)
      if (event.key === 'ArrowRight') step(1)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, step])

  const label = String(cellData ?? row.filename ?? row.id)

  return (
    <>
      <button
        aria-label={`Anteprima di ${label}`}
        className="default-cell__first-cell"
        onClick={open}
        title="Apri l'anteprima grande"
        type="button"
      >
        <div className="file">
          <Thumbnail
            className="file__thumbnail"
            collectionSlug={collectionSlug}
            doc={row}
            fileSrc={thumbSrc(row)}
            imageCacheTag={row.updatedAt ?? undefined}
            size="small"
          />
          <span className="file__filename">{label}</span>
        </div>
      </button>
      {isOpen && (
        <Modal
          aria-label="Anteprima dell'immagine"
          closeOnBlur={false}
          slug={modalSlug}
          style={{ zIndex: PREVIEW_Z }}
        >
          <div style={styles.backdrop}>
            <div style={styles.card}>
              <div style={styles.stage}>
                <div style={styles.close}>
                  <Button
                    aria-label="Chiudi l'anteprima"
                    buttonStyle="secondary"
                    icon={<XIcon />}
                    margin={false}
                    onClick={() => closeModal(modalSlug)}
                    round
                    size="small"
                  />
                </div>
                <PreviewImage doc={doc} />
                {index > 0 && (
                  <div style={{ ...styles.nav, left: '0.5rem' }}>
                    <Button
                      aria-label="Immagine precedente"
                      buttonStyle="secondary"
                      icon={<ChevronIcon direction="left" />}
                      margin={false}
                      onClick={() => step(-1)}
                      round
                    />
                  </div>
                )}
                {index > -1 && index < docs.length - 1 && (
                  <div style={{ ...styles.nav, right: '0.5rem' }}>
                    <Button
                      aria-label="Immagine successiva"
                      buttonStyle="secondary"
                      icon={<ChevronIcon direction="right" />}
                      margin={false}
                      onClick={() => step(1)}
                      round
                    />
                  </div>
                )}
              </div>
              <div style={styles.footer}>
                <div style={{ minWidth: 0 }}>
                  <div style={styles.filename}>{doc.filename ?? doc.id}</div>
                  <div style={styles.meta}>{describe(doc, index, docs.length)}</div>
                  {doc.alt ? <div style={styles.alt}>Alt: {doc.alt}</div> : null}
                </div>
                <div style={styles.actions}>
                  <Button
                    buttonStyle="secondary"
                    margin={false}
                    onClick={() => closeModal(modalSlug)}
                    size="medium"
                  >
                    Annulla
                  </Button>
                  <Button
                    margin={false}
                    onClick={() => {
                      closeModal(modalSlug)
                      onSelect({ collectionSlug, doc, docID: String(doc.id) })
                    }}
                    size="medium"
                  >
                    Usa questa immagine
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

/** `key` sull'src: sfogliando, il browser non tiene appesa l'immagine precedente. */
function PreviewImage({ doc }: { doc: MediaDoc }) {
  const src = largeSrc(doc)
  if (!src) return <div style={styles.empty}>Nessuna anteprima disponibile per questo file.</div>
  return (
    <img
      alt={doc.alt ?? doc.filename ?? ''}
      key={src}
      src={withCacheTag(src, doc.updatedAt)}
      style={styles.image}
    />
  )
}

function describe(doc: MediaDoc, index: number, total: number): string {
  const parts: string[] = []
  if (doc.width && doc.height) parts.push(`${doc.width} × ${doc.height} px`)
  if (doc.filesize) parts.push(formatFilesize(doc.filesize))
  if (doc.mimeType) parts.push(doc.mimeType.replace('image/', '').toUpperCase())
  if (doc.aiGenerated) parts.push('generata da AI')
  if (index > -1 && total > 1) parts.push(`${index + 1} di ${total}`)
  return parts.join(' · ')
}
